import { Card, CardBody, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '../../../components';
import { ProfessionAddOnTrack } from '../../../common/Types';
import Skeleton from 'react-loading-skeleton';
import { professionService } from '../../../services/ProfessionAdministration';
import { toast } from 'react-toastify';

interface AddOnTracksCardProps {
    professionId: number;
}

const AddOnTracksCard = (props: AddOnTracksCardProps) => {
    const { professionId } = props;

    const [addOnTracks, setAddOnTracks] = useState<ProfessionAddOnTrack[]>();
    const [reloadTracks, setReloadTracks] = useState<boolean>(true);

    const [addOnTrackEdit, setAddOnTrackEdit] = useState<ProfessionAddOnTrack>();
    const [showModal, setShowModal] = useState<boolean>(false);
    const toggleModal = () => setShowModal(!showModal);

    const [addOnTrackToDelete, setAddOnTrackToDelete] = useState<ProfessionAddOnTrack>();
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const toggleDeleteModal = () => setShowDeleteModal(!showDeleteModal);

    const activeAddOnTracks = useMemo(() => {
        return addOnTracks?.filter((a) => !a.isDeleted).sort((a, b) => (a.title || '').localeCompare(b.title || '')) || [];
    }, [addOnTracks]);

    const deletedAddOnTracks = useMemo(() => {
        return addOnTracks?.filter((a) => a.isDeleted).sort((a, b) => (a.title || '').localeCompare(b.title || '')) || [];
    }, [addOnTracks]);

    useEffect(() => {
        const getAddOnTracks = () => {
            if (professionId && reloadTracks) {
                setReloadTracks(false);
                professionService
                    .getAddOnTracksByProfessionId(professionId)
                    .then((result) => {
                        setAddOnTracks([...result]);
                    })
                    .catch(() => {
                        toast.error('Error loading add-on tracks');
                    });
            }
        };

        getAddOnTracks();
    }, [professionId, setAddOnTracks, reloadTracks]);

    const startNewAddOnTrack = () => {
        setAddOnTrackEdit({
            addOnTrackId: 0,
            professionId: professionId,
            title: '',
        });
        toggleModal();
    };

    const startEditAddOnTrack = (addOnTrack: ProfessionAddOnTrack) => {
        setAddOnTrackEdit({
            ...addOnTrack,
        });
        toggleModal();
    };

    const saveAddOnTrack = () => {
        if (addOnTrackEdit && addOnTrackEdit.title) {
            const toastId = toast.info('Saving add-on track...');

            professionService
                .saveAddOnTrack(addOnTrackEdit)
                .then(() => {
                    toast.update(toastId, {
                        type: 'success',
                        render: 'Add-on track saved successfully',
                    });

                    setReloadTracks(true);
                    toggleModal();
                })
                .catch(() => {
                    toast.update(toastId, {
                        type: 'error',
                        render: 'Error saving add-on track',
                    });
                });
        }

        if (addOnTrackEdit && !addOnTrackEdit.title) {
            toast.error('Add-On Track Title is required');
        }
    };

    const confirmDeleteAddOnTrack = (addOnTrack: ProfessionAddOnTrack) => {
        setAddOnTrackToDelete(addOnTrack);
        setShowDeleteModal(true);
    };

    const deleteAddOnTrack = () => {
        if (!addOnTrackToDelete) return;

        const toastId = toast.info('Deleting add-on track...');

        professionService
            .saveAddOnTrack({ ...addOnTrackToDelete, isDeleted: true })
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Add-on track deleted successfully',
                });
                setReloadTracks(true);
                setShowDeleteModal(false);
                setAddOnTrackToDelete(undefined);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error deleting add-on track',
                });
            });
    };

    const restoreAddOnTrack = (addOnTrack: ProfessionAddOnTrack) => {
        const toastId = toast.info('Restoring add-on track...');

        professionService
            .saveAddOnTrack({ ...addOnTrack, isDeleted: false })
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Add-on track restored successfully',
                });
                setReloadTracks(true);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error restoring add-on track',
                });
            });
    };

    return (
        <>
            <Card>
                <CardBody className={'d-flex flex-column'}>
                    <h4 className={'card-title d-flex align-items-center justify-content-between'}>
                        <span>{`Add-On Tracks`}</span>
                        <div>
                            <Button
                                type={'button'}
                                color={'primary'}
                                className={'btn-icon'}
                                onClick={() => startNewAddOnTrack()}
                            >
                                <i className={`mdi mdi-plus`} />
                            </Button>
                        </div>
                    </h4>
                    {!addOnTracks && <Skeleton count={5} />}
                    {addOnTracks && activeAddOnTracks.length <= 0 && <Label>{`No Add-On Tracks`}</Label>}
                    {activeAddOnTracks.length > 0 &&
                        activeAddOnTracks.map((t) => (
                            <div
                                key={t.addOnTrackId}
                                className={'d-flex justify-content-between align-items-center mb-2'}
                            >
                                <span>{t.title}</span>
                                <div>
                                    <Button
                                        type={'button'}
                                        color={'info'}
                                        className={'btn-icon mr-1'}
                                        onClick={() => startEditAddOnTrack(t)}
                                    >
                                        <i className={'mdi mdi-pencil'} />
                                    </Button>
                                    <Button
                                        type={'button'}
                                        color={'danger'}
                                        className={'btn-icon'}
                                        onClick={() => confirmDeleteAddOnTrack(t)}
                                    >
                                        <i className={'mdi mdi-trash-can-outline'} />
                                    </Button>
                                </div>
                            </div>
                        ))}

                    {deletedAddOnTracks.length > 0 && (
                        <>
                            <hr />
                            <h5 className={'text-muted'}>{`Deleted Add-On Tracks`}</h5>
                            {deletedAddOnTracks.map((t) => (
                                <div
                                    key={t.addOnTrackId}
                                    className={'d-flex justify-content-between align-items-center mb-2 text-muted'}
                                >
                                    <span>{t.title}</span>
                                    <div>
                                        <Button
                                            type={'button'}
                                            color={'secondary'}
                                            className={'btn-icon'}
                                            onClick={() => restoreAddOnTrack(t)}
                                            title={'Restore'}
                                        >
                                            <i className={'mdi mdi-restore'} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </CardBody>
            </Card>

            {addOnTrackEdit && (
                <Modal isOpen={showModal} toggle={toggleModal} centered={true}>
                    <ModalHeader>
                        {addOnTrackEdit.addOnTrackId > 0 ? `Edit Add-On Track` : `New Add-On Track`}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>{`Add-On Track Title`}</Label>
                            <Input
                                type={'text'}
                                value={addOnTrackEdit.title}
                                onChange={(e) => setAddOnTrackEdit({ ...addOnTrackEdit, title: e.target.value })}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color={'primary'}
                            className={'mr-2'}
                            type={'button'}
                            onClick={() => saveAddOnTrack()}
                        >{`Submit`}</Button>
                        <Button color={'link'} onClick={() => toggleModal()} type={'button'}>{`Cancel`}</Button>
                    </ModalFooter>
                </Modal>
            )}

            <Modal isOpen={showDeleteModal} toggle={toggleDeleteModal} centered={true}>
                <ModalHeader>{`Delete Add-On Track`}</ModalHeader>
                <ModalBody>
                    {`Are you sure you want to delete the add-on track "${addOnTrackToDelete?.title}"?`}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color={'danger'}
                        className={'mr-2'}
                        type={'button'}
                        onClick={() => deleteAddOnTrack()}
                    >{`Delete`}</Button>
                    <Button color={'link'} onClick={() => toggleDeleteModal()} type={'button'}>{`Cancel`}</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default AddOnTracksCard;
